/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
// Store
import useStore from '@store/index';

// Types
import { TSelectOption } from '@global-types/index';
import {
	useDebouncedValue,
	useElementSize,
} from '@mantine/hooks';
import dayjs from 'dayjs';
import {
	TProduction,
	TProductionSortColumn,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

type ObjWithStartDate = {
	startDate: {
		$gte: string;
		$lt: string;
	};
};

/**
 * Custom hook for managing offset list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useProduction = () => {
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});
	const [resetFilterSites, setResetFilterSites] =
		useState(false);

	const [isOverflowFilters] = useState(false);

	const [openDrawer, setOpenDrawer] = useState(false);

	const [
		openDrawerAddProduction,
		setOpenDrawerAddProduction,
	] = useState(false);

	const [
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
	] = useState(false);

	const [
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
	] = useState(false);

	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const [dateFilterType, setDateFilterType] =
		useState('');

	const {
		productionsPage,
		productionsPageSize,
		setProductionsPage,
		productionsSelectedRecords,
		setProductionsSelectedRecords,
		productionsOpenConfirmationModalDelete,
		setProductionsOpenConfirmationModalDelete,
		productionsOpenConfirmationModalVerify,
		setProductionsOpenConfirmationModalVerify,
		productionsOpenConfirmationModalMint,
		setProductionsOpenConfirmationModalMint,
		productionsQueryParams,
		setProductionsQueryParams,
		productionsSortStatus,
		setProductionsSortStatus,
		setProductionsSortParams,
		productionsSortParams,
		setProductionsPageSize,
		setProductionsSelectedRow,
		productionsSelectedRow,
		setIsOpenStartMint,
		setProductionsStartMintsActiveStep,
	} = useStore();
	const containerRef = useRef<HTMLDivElement>(null);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleSortParams = (
		_column: TProductionSortColumn
	) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'production':
				sortParams = 'production';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setProductionsSortParams(sortParams);
		} else {
			setProductionsSortParams('-' + sortParams);
		}
	};

	const [debouncedPageSize] = useDebouncedValue(
		productionsPageSize,
		1000
	);

	const handleStartMint = () => {
		setProductionsStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const handleReset = () => {
		setProductionsPageSize(100);
		setProductionsQueryParams({});
		setProductionsPage(1);
		setProductionsSortParams('-createdAt');
		setProductionsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hasStartDate(obj: ObjWithStartDate) {
		if (
			Object.keys(obj).length === 0 &&
			obj.constructor === Object
		) {
			return false;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte !== undefined &&
			obj.startDate.$lt !== undefined
		) {
			return true;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte === undefined &&
			obj.startDate.$lt === undefined
		) {
			return false;
		}

		return false;
	}

	const NBR_SELECTED_ROWS =
		productionsSelectedRecords.length;

	const selectedFields = [
		'_id',
		'project.name',
		'site.name',
		'startDate',
		'quantity',
		'verified',
		'quantityCheck',
		'status',
	];

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Drawer add production ****************************/

	const handleCloseDrawerAddProduction = () => {
		setOpenDrawerAddProduction(false);
	};

	/**************************** Drawer show more production ****************************/

	const handleverifyproduction = (
		item: TProduction,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		verify: any
	) => {
		const cloneSelectedRow = {
			...productionsSelectedRow,
		};

		if (!cloneSelectedRow.verified) {
			cloneSelectedRow.verified = !item;
			verify.mutate({
				ids: [cloneSelectedRow.id],
				verified: cloneSelectedRow.verified,
			});
			setProductionsSelectedRow(cloneSelectedRow);
		} else {
			setProductionsOpenConfirmationModalVerify({
				open: true,
				params: {
					ids: [cloneSelectedRow.id],
					verified: !cloneSelectedRow.verified,
				},
			});
		}
	};

	const handledeleteproduction = () => {
		setProductionsOpenConfirmationModalDelete({
			open: true,
			params: productionsSelectedRow,
		});
	};

	const handleCloseDrawerShowMoreProduction = () => {
		setOpenDrawer(false);
	};

	const handlestartmint = () => {
		if (
			!productionsSelectedRow ||
			!productionsSelectedRow.projectId ||
			!productionsSelectedRow.id
		) {
			console.error(
				'Les données de ligne sont incomplètes ou incorrectes.'
			);
			return;
		}

		const mintBody = {
			project: productionsSelectedRow.projectId,
			productions: [productionsSelectedRow.id],
		};

		setProductionsOpenConfirmationModalMint({
			open: true,
			params: mintBody,
		});
	};

	/**************************** Download Multiple selection modal ****************************/

	const handledownloadmultipleproductions = (
		user: any,
		download: any
	) => {
		const selectedProductionsIds = [
			...productionsSelectedRecords,
		].map((item) => item.id);

		const currentUser = user;

		const body: any = {
			fields: selectedFields,
			owner: currentUser ? currentUser.id : '',
			productions: selectedProductionsIds,
		};

		download.mutate(body);

		setProductionsSelectedRecords([]);
		setOpenSelectedItemsToDownloadModal(false);
	};

	const handleclosedownloadmultipleselection = () => {
		setOpenSelectedItemsToDownloadModal(false);
	};

	const handleremovedownloadmultipleselection = (
		item: any
	) => {
		const newList = [
			...productionsSelectedRecords,
		].filter((production) => production.id !== item.id);

		setProductionsSelectedRecords(newList);
	};

	/**************************** Verify Multiple selection modal ****************************/

	const handleverifymultipleselectionconfirm = (
		verify: any
	) => {
		const idsList = [...productionsSelectedRecords]
			.filter((item1) => !item1.verified)
			.map((item2) => item2.id);
		verify.mutate({
			ids: idsList,
			verified: true,
		});
		setProductionsSelectedRecords([]);
		setOpenSelectedItemsToVerifyModal(false);
	};

	const handlecloseverifymultipleselectionmodal = () => {
		setOpenSelectedItemsToVerifyModal(false);
	};

	const handleremoveverifymultipleselection = (
		item: any
	) => {
		const newList = [
			...productionsSelectedRecords,
		].filter((production) => production.id !== item.id);

		setProductionsSelectedRecords(newList);
	};

	/**************************** Delete Multiple selection modal ****************************/

	const handledeletemultipleselectionconfirm = (
		removeMultiple: any
	) => {
		const idsList = [...productionsSelectedRecords]
			.filter((item1) => !item1.verified)
			.map((item2) => item2.id);
		removeMultiple.mutate({
			ids: idsList,
		});

		setProductionsSelectedRecords([]);
		setOpenSelectedItemsToDeleteModal(false);
	};

	const handleclosedeletemultipleselectionmodal = () => {
		setOpenSelectedItemsToDeleteModal(false);
	};

	const handleremovedeletemultipleselection = (
		item: any
	) => {
		const newList = [
			...productionsSelectedRecords,
		].filter((production) => production.id !== item.id);

		setProductionsSelectedRecords(newList);
	};

	/**************************** Delete confirmation modal ****************************/

	const handledeleteconfirmationmodalconfirm = (
		remove: any
	) => {
		setProductionsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			productionsOpenConfirmationModalDelete.params.id
		);
		setOpenDrawer(false);
		setProductionsSelectedRecords([]);
	};

	const handleclosedeleteconfirmationmodal = () => {
		setProductionsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	/**************************** Verify confirmation modal ****************************/

	const handleverifyconfirmationmodalconfirm = (
		verify: any
	) => {
		setProductionsOpenConfirmationModalVerify({
			open: false,
			params: {},
		});

		if (productionsOpenConfirmationModalVerify) {
			verify.mutate(
				productionsOpenConfirmationModalVerify.params
			);
		}
		const rowId =
			productionsOpenConfirmationModalVerify.params
				.ids[0];
		const _productionsSelectedRecords =
			productionsSelectedRecords.map((item: any) => {
				if (item.id == rowId) {
					item.verified = false;
				}
				return item;
			});

		setProductionsSelectedRecords(
			_productionsSelectedRecords
		);

		setProductionsSelectedRow({
			...productionsSelectedRow,
			verified: false,
		});
		setProductionsSelectedRecords([]);
	};

	const handlecloseverifyconfirmationmodal = () => {
		setProductionsOpenConfirmationModalVerify({
			open: false,
			params: {},
		});
	};

	/**************************** Mint confirmation modal ****************************/

	const handlemintconfirmationmodalconfirm = (
		mint: any
	) => {
		if (productionsOpenConfirmationModalMint) {
			mint.mutate(
				productionsOpenConfirmationModalMint.params
			);
		}
		setProductionsOpenConfirmationModalMint({
			open: false,
			params: {},
		});
		setOpenDrawer(false);
		setProductionsSelectedRecords([]);
	};

	const handleclosemintconfirmationmodal = () => {
		setProductionsOpenConfirmationModalMint({
			open: false,
			params: {},
		});
	};

	/**************************** Datatable ****************************/

	const handlerowclicked = (row: any) => {
		setOpenDrawer(true);
		setProductionsSelectedRow(row.record);
	};

	const handlerowviewmore = (row: any) => {
		setProductionsSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleVerifyProduction = (
		row: TProduction,
		verify: any
	) => {
		if (!row.verified) {
			verify.mutate({
				ids: [row.id],
				verified: !row.verified,
			});
			const _productionsSelectedRecords =
				productionsSelectedRecords.map((item: any) => {
					if (item.id == row.id) {
						item.verified = !row.verified;
					}
					return item;
				});

			setProductionsSelectedRecords(
				_productionsSelectedRecords
			);
			setProductionsSelectedRecords([]);
		} else {
			setProductionsOpenConfirmationModalVerify({
				open: true,
				params: {
					ids: [row.id],
					verified: !row.verified,
				},
			});
		}
	};

	const handlerowmint = (row: any) => {
		if (!row || !row.projectId || !row.id) {
			console.error(
				'The line data is incomplete or incorrect.'
			);
			return;
		}
		setProductionsSelectedRow(row);
		const mintBody = {
			project: row.projectId,
			productions: [row.id],
		};
		setProductionsOpenConfirmationModalMint({
			open: true,
			params: mintBody,
		});
	};

	const handlerowremoveitem = (row: any) => {
		setProductionsOpenConfirmationModalDelete({
			open: true,
			params: row,
		});
	};

	const handlechangesortstatus = (data: any) => {
		handleSortParams(data);
		setProductionsSortStatus(data);
	};

	const handleselectedrecordchange = (
		selectedRecords: any
	) => {
		setProductionsSelectedRecords(selectedRecords);
	};

	/**************************** Pagination ****************************/

	const handlepaginationchangesize = function (
		_size: string | number
	) {
		setProductionsPageSize(_size);
		setProductionsPage(1);
	};

	const handlechangepagination = (data: any) => {
		setProductionsPage(data);
	};

	/**************************** Filters ****************************/
	const handleapplydatefilter = (date: any) => {
		setDateFilterType(date[0].label);
		setProductionsSelectedRecords([]);
		let startDate;
		let endDate;
		if (
			dayjs(date[0].value[0]).isValid() &&
			dayjs(date[0].value[1]).isValid()
		) {
			startDate = date[0].value[0];
			endDate = date[0].value[1];
		} else {
			startDate = dayjs()
				.subtract(364, 'days')
				.toISOString();
			endDate = dayjs().toISOString();
		}

		const queryParamsDate = {
			startDate: {
				$gte: startDate,
				$lt: endDate,
			},
		};

		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsDate,
		});
		setProductionsPage(1);
	};

	const handleapplyprojectfilter = (
		projects: TSelectOption[]
	) => {
		setProductionsSelectedRecords([]);
		setProductionsPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			site: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsProjects,
		});
		setResetFilterSites(true);
	};

	const handleapplystatusfilter = (status: any) => {
		setProductionsSelectedRecords([]);
		setProductionsPage(1);
		const queryParamsVerified = {
			verified: status.map(
				(_status: any) => _status.value
			),
		};
		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsVerified,
		});
	};

	const handleapplysitefilter = (sites: any) => {
		setProductionsSelectedRecords([]);
		setProductionsPage(1);
		const queryParamsSites = {
			site: sites.map((_site: any) => _site.value),
		};

		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsSites,
		});
	};

	const handleapplymintstatusfilter = (
		mintStatusList: any
	) => {
		setProductionsSelectedRecords([]);
		setProductionsPage(1);
		const queryParamsMintStatus = {
			status: Array.isArray(mintStatusList)
				? mintStatusList.map(
						(_mintStatus: any) => _mintStatus.value
					)
				: [],
		};

		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsMintStatus,
		});
	};

	const handleapplyquantitycheckfilter = (
		quantityCheckList: TSelectOption[]
	) => {
		setProductionsSelectedRecords([]);
		setProductionsPage(1);
		const queryParamsQuantityCheck = {
			quantityCheck: Array.isArray(quantityCheckList)
				? quantityCheckList.map(
						(_quantityCheck: TSelectOption) =>
							_quantityCheck.value
					)
				: [],
		};
		setProductionsQueryParams({
			...productionsQueryParams,
			...queryParamsQuantityCheck,
		});
	};

	/**************************** Actions ****************************/

	const handledownloadaction = (
		user: any,
		download: any
	) => {
		const currentUser: any = user;
		if (productionsSelectedRecords.length === 0) {
			const body: any = {
				fields: selectedFields,
				owner: currentUser ? currentUser.id : '',
			};
			if (productionsQueryParams) {
				if (productionsQueryParams.startDate) {
					if (
						productionsQueryParams.startDate['$gte'] &&
						productionsQueryParams.startDate['$lt']
					) {
						body.from = dayjs(
							productionsQueryParams.startDate['$gte']
						).format('YYYY-MM-DD');

						if (dateFilterType == 'custom') {
							body.to = dayjs(
								productionsQueryParams.startDate['$lt']
							).format('YYYY-MM-DD');
						} else {
							body.to = dayjs(
								productionsQueryParams.startDate['$lt']
							).format('YYYY-MM-DD');
						}
					}
				}
			}

			download.mutate(body);
		} else {
			setOpenSelectedItemsToDownloadModal(true);
		}
	};

	const handleResetSelectedItems = () => {
		setProductionsSelectedRecords([]);
	};
	/********************************************************************************************/

	return {
		handleResetSelectedItems,
		handledownloadaction,
		handleapplymintstatusfilter,
		handleapplysitefilter,
		handleapplyprojectfilter,
		handleapplystatusfilter,
		handleapplydatefilter,
		handlechangepagination,
		handlepaginationchangesize,
		handleselectedrecordchange,
		handlechangesortstatus,
		handlerowremoveitem,
		handleVerifyProduction,
		handlerowviewmore,
		handlerowmint,
		handlerowclicked,
		handleclosemintconfirmationmodal,
		handlemintconfirmationmodalconfirm,
		handlecloseverifyconfirmationmodal,
		handleverifyconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handledeleteconfirmationmodalconfirm,
		handleremovedeletemultipleselection,
		handleclosedeletemultipleselectionmodal,
		handledeletemultipleselectionconfirm,
		handleremoveverifymultipleselection,
		handlecloseverifymultipleselectionmodal,
		containerRef,
		handleverifymultipleselectionconfirm,
		handleCloseDrawerAddProduction,
		productionsPage,
		productionsPageSize,
		setProductionsPage,
		productionsSelectedRecords,
		setProductionsSelectedRecords,
		productionsOpenConfirmationModalDelete,
		setProductionsOpenConfirmationModalDelete,
		productionsOpenConfirmationModalVerify,
		setProductionsOpenConfirmationModalVerify,
		productionsOpenConfirmationModalMint,
		setProductionsOpenConfirmationModalMint,
		productionsQueryParams,
		setProductionsQueryParams,
		productionsSortStatus,
		setProductionsSortStatus,
		setProductionsSortParams,
		productionsSortParams,
		setProductionsPageSize,
		setProductionsSelectedRow,
		productionsSelectedRow,
		setIsOpenStartMint,
		setProductionsStartMintsActiveStep,
		queryParamsProjects,
		setQueryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		openDrawer,
		setOpenDrawer,
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddProduction,
		setOpenDrawerAddProduction,
		handleSortParams,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		selectedFields,
		ref,
		handleverifyproduction,
		handledeleteproduction,
		handleCloseDrawerShowMoreProduction,
		handlestartmint,
		handledownloadmultipleproductions,
		handleclosedownloadmultipleselection,
		handleremovedownloadmultipleselection,
		handleapplyquantitycheckfilter,
		debouncedPageSize,
	};
};
